import { useEffect, useRef, useState } from 'react'
import { Button } from '../button/button'
import {
  ChipModal,
  ChipModalBody,
  ChipModalFooter,
  ChipModalHeader,
} from '../chip-modal/chip-modal'
import { DropdownMenuItem } from '../dropdown-menu/dropdown-menu'
import { Input } from '../input/input'
import { Label } from '../label/label'
import { SplitButton } from '../split-button/split-button'
import './media-editor.css'

export interface MediaEntry {
  id: string
  url: string
  name: string
  type: string
  alt: string
}
export interface MediaEditorProps {
  /** Open the workspace library directly, without inline upload controls. */
  libraryOnly?: boolean
  onLibraryClose?: () => void
  onGenerate?: () => void
  generateLabel?: string
  allowUrl?: boolean
  onBusyChange?: (busy: boolean) => void
  value: MediaEntry[]
  onChange: (items: MediaEntry[]) => void
  disabled?: boolean
  accept?: string
  maxCount?: number
  locale?: 'en' | 'zh'
  upload: (file: File) => Promise<MediaEntry>
  library: MediaEntry[]
  libraryLoading?: boolean
  libraryError?: string
  refreshLibrary: () => void
}
export function MediaEditor({
  libraryOnly = false,
  onLibraryClose,
  onGenerate,
  generateLabel,
  allowUrl = true,
  onBusyChange,
  value,
  onChange,
  disabled,
  accept = 'image/*,video/*',
  maxCount = 20,
  locale = 'en',
  upload,
  library,
  libraryLoading,
  libraryError,
  refreshLibrary,
}: MediaEditorProps) {
  const tr = (en: string, zh: string) => (locale === 'zh' ? zh : en)
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])
  const fileInput = useRef<HTMLInputElement>(null)
  const latest = useRef(value)
  latest.current = value
  const [dialog, setDialog] = useState<'url' | 'library' | null>(libraryOnly ? 'library' : null)
  const [url, setUrl] = useState('')
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<string[]>([])
  const [error, setError] = useState('')
  const [busy, setBusy] = useState('')
  useEffect(() => {
    onBusyChange?.(!!busy)
    return () => onBusyChange?.(false)
  }, [busy, onBusyChange])
  const accepts = (type: string) =>
    accept
      .split(',')
      .some((rule) =>
        rule.trim().endsWith('/*')
          ? type.startsWith(rule.trim().slice(0, -1))
          : type === rule.trim()
      )
  function append(items: MediaEntry[]) {
    const merged = [...latest.current]
    for (const item of items) if (!merged.some((x) => x.id === item.id)) merged.push(item)
    if (merged.length > maxCount) {
      setError(tr(`Select up to ${maxCount} files.`, `最多选择 ${maxCount} 个文件。`))
      return false
    }
    latest.current = merged
    onChange(merged)
    setError('')
    return true
  }
  async function receive(files: File[]) {
    if (disabled || busy) return
    setError('')
    for (const file of files) {
      if (latest.current.length >= maxCount) {
        setError(tr(`Select up to ${maxCount} files.`, `最多选择 ${maxCount} 个文件。`))
        break
      }
      if (!accepts(file.type)) {
        setError(tr('Unsupported media type.', '不支持此素材类型。'))
        break
      }
      setBusy(file.name)
      try {
        const entry = await upload(file)
        if (!alive.current) return
        append([entry])
      } catch (e) {
        setError(
          e instanceof Error ? e.message : tr('Upload failed. Try again.', '上传失败，请重试。')
        )
        break
      }
    }
    setBusy('')
  }
  function addUrl() {
    try {
      const parsed = new URL(url.trim())
      if (parsed.protocol !== 'https:' || parsed.username || parsed.password) throw new Error()
      const type = /\.(mp4|webm|mov)$/i.test(parsed.pathname)
        ? 'video/mp4'
        : /\.(mp3|wav|ogg)$/i.test(parsed.pathname)
          ? 'audio/mpeg'
          : 'image/*'
      if (!accepts(type)) throw new Error()
      if (
        append([
          {
            id: parsed.href,
            url: parsed.href,
            name: parsed.pathname.split('/').pop() || parsed.hostname,
            type,
            alt: '',
          },
        ])
      ) {
        setDialog(null)
        setUrl('')
      }
    } catch {
      setError(
        tr('Enter an HTTPS media URL of an accepted type.', '请输入符合类型要求的 HTTPS 素材链接。')
      )
    }
  }
  const candidates = library.filter(
    (item) => accepts(item.type) && `${item.name}`.toLowerCase().includes(search.toLowerCase())
  )
  const preview = (item: MediaEntry) =>
    item.type.startsWith('video/') ? (
      // biome-ignore lint/a11y/useMediaCaption: Previewing user-selected files; captions may not exist.
      <video src={item.url} preload='metadata' controls aria-label={item.name} />
    ) : item.type.startsWith('audio/') ? (
      // biome-ignore lint/a11y/useMediaCaption: Previewing user-selected files; captions may not exist.
      <audio src={item.url} preload='metadata' controls aria-label={item.name} />
    ) : (
      <img src={item.url} alt={item.alt || item.name} loading='lazy' referrerPolicy='no-referrer' />
    )
  return (
    <div className='media-editor'>
      <fieldset disabled={disabled || !!busy} className='media-editor-controls' style={libraryOnly ? { display: 'none' } : undefined}>
        <input
          ref={fileInput}
          type='file'
          accept={accept}
          multiple
          hidden
          onChange={(e) => {
            void receive(Array.from(e.target.files ?? []))
            e.target.value = ''
          }}
        />
        <SplitButton
          align='start'
          action={tr('Upload files', '上传文件')}
          menuLabel={tr('More ways to add media', '更多添加方式')}
          onClick={() => fileInput.current?.click()}
          disabled={value.length >= maxCount}
          menuDisabled={value.length >= maxCount}
        >
          {allowUrl && (
            <DropdownMenuItem
              onSelect={() => {
                setError('')
                setDialog('url')
              }}
            >
              {tr('Add URL', '填写链接')}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onSelect={() => {
              setError('')
              setSelected([])
              setDialog('library')
              refreshLibrary()
            }}
          >
            {tr('Choose from Files', '从工作区文件选择')}
          </DropdownMenuItem>
          {onGenerate && (
            <DropdownMenuItem onSelect={onGenerate}>
              {generateLabel ?? tr('Generate media', '生成素材')}
            </DropdownMenuItem>
          )}
        </SplitButton>
        <span className='media-editor-hint'>
          {value.length} / {maxCount}
        </span>
      </fieldset>
      {busy && (
        <p role='status'>
          {tr('Uploading', '正在上传')} · {busy}
        </p>
      )}
      {error && !dialog && (
        <p role='alert' className='media-editor-error'>
          {error}
        </p>
      )}
      <div className='media-editor-items'>
        {value.map((item, index) => (
          <div className='media-editor-item' key={item.id}>
            <div className='media-editor-preview'>{preview(item)}</div>
            <div className='media-editor-meta'>
              <span title={item.name}>{item.name}</span>
              <Input
                aria-label={`${tr('Description', '素材说明')} · ${item.name}`}
                value={item.alt}
                disabled={disabled || !!busy}
                placeholder={tr('Description (optional)', '素材说明（可选）')}
                onChange={(e) =>
                  onChange(value.map((x) => (x.id === item.id ? { ...x, alt: e.target.value } : x)))
                }
              />
              <div className='media-editor-controls'>
                <Button
                  disabled={disabled || !!busy || index === 0}
                  onClick={() => {
                    const next = [...value]
                    ;[next[index - 1], next[index]] = [next[index], next[index - 1]]
                    onChange(next)
                  }}
                >
                  {tr('Move earlier', '前移')}
                </Button>
                <Button
                  disabled={disabled || !!busy}
                  onClick={() => onChange(value.filter((x) => x.id !== item.id))}
                >
                  {tr('Remove', '移除')}
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <ChipModal
        open={dialog !== null}
        onOpenChange={(open) => {
          if (!open) { setDialog(null); onLibraryClose?.() }
        }}
        srTitle={tr('Add media', '添加素材')}
      >
        <ChipModalHeader onClose={() => { setDialog(null); onLibraryClose?.() }}>
          {dialog === 'url'
            ? tr('Add media URL', '填写素材链接')
            : tr('Choose from Files', '从工作区文件选择')}
        </ChipModalHeader>
        <ChipModalBody>
          <fieldset disabled={disabled} className='media-editor'>
            {dialog === 'url' ? (
              <>
                <Label htmlFor='media-source-url'>{tr('HTTPS media URL', 'HTTPS 素材链接')}</Label>
                <Input
                  id='media-source-url'
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder='https://'
                />
              </>
            ) : (
              <>
                <Input
                  aria-label={tr('Search files', '搜索文件')}
                  placeholder={tr('Search files', '搜索文件')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {libraryLoading && <p role='status'>{tr('Loading files…', '正在加载文件…')}</p>}
                {libraryError && <p role='alert'>{libraryError}</p>}
                {!libraryLoading && !libraryError && !candidates.length && (
                  <p>{tr('No matching media files.', '没有匹配的素材文件。')}</p>
                )}
                <div className='media-editor-library'>
                  {candidates.map((item) => (
                    <label key={item.id} className='media-editor-library-row'>
                      <input
                        type='checkbox'
                        checked={selected.includes(item.id) || value.some((x) => x.id === item.id)}
                        disabled={value.some((x) => x.id === item.id)}
                        onChange={(e) =>
                          setSelected((old) =>
                            e.target.checked
                              ? [...old, item.id]
                              : old.filter((id) => id !== item.id)
                          )
                        }
                      />
                      <span>{item.name}</span>
                      <span className='media-editor-hint'>{item.type}</span>
                    </label>
                  ))}
                </div>
              </>
            )}
            {error && (
              <p role='alert' className='media-editor-error'>
                {error}
              </p>
            )}
          </fieldset>
        </ChipModalBody>
        <ChipModalFooter
          cancelLabel={tr('Cancel', '取消')}
          onCancel={() => { setDialog(null); onLibraryClose?.() }}
          primaryAction={{
            label: tr('Add selected media', '添加素材'),
            disabled: disabled || (dialog === 'url' ? !url.trim() : !selected.length),
            onClick: () => {
              if (dialog === 'url') addUrl()
              else if (append(library.filter((x) => selected.includes(x.id)))) setDialog(null)
            },
          }}
        />
      </ChipModal>
    </div>
  )
}
